import { useEffect, useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import type { AppointmentWithDetails, Veterinarian } from "@/hooks/useAppointments";
import type { AppointmentStatus } from "@/hooks/useAppointmentStatuses";
import { deriveFinancialStatusWithLegacyFallback } from "@/lib/utils/appointment-financial";
import { getStatusColor } from "@/lib/utils/clinic-appointment-status";
import { AppointmentKanbanCard } from "./AppointmentKanbanCard";

const DEBOUNCE_OVER_MS = 60;
const DEBOUNCE_LEAVE_MS = 100;

interface DroppableStatusColumnProps {
  status: AppointmentStatus;
  appointments: AppointmentWithDetails[];
  statuses?: AppointmentStatus[];
  professionals?: Veterinarian[];
  onAppointmentClick?: (appointmentId: string) => void;
  showAppointmentItemsOnCards?: boolean;
}

export function DroppableStatusColumn({
  status,
  appointments,
  statuses,
  professionals,
  onAppointmentClick,
  showAppointmentItemsOnCards = false,
}: DroppableStatusColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: status.name,
  });
  const [displayOver, setDisplayOver] = useState(false);

  useEffect(() => {
    if (isOver) {
      const t = window.setTimeout(() => setDisplayOver(true), DEBOUNCE_OVER_MS);
      return () => clearTimeout(t);
    }
    const t = window.setTimeout(() => setDisplayOver(false), DEBOUNCE_LEAVE_MS);
    return () => clearTimeout(t);
  }, [isOver]);

  const groupedAppointments =
    status.name === "finalizado"
      ? {
        unsettled: appointments.filter(
          (appointment) =>
            deriveFinancialStatusWithLegacyFallback(appointment.appointment_financial, {
              status: appointment.status,
              grooming_status: null,
            }, appointment.linked_sale) !== "pago",
        ),
        settled: appointments.filter(
            (appointment) =>
            deriveFinancialStatusWithLegacyFallback(appointment.appointment_financial, {
              status: appointment.status,
              grooming_status: null,
            }, appointment.linked_sale) === "pago",
        ),
      }
      : {
        unsettled: appointments,
        settled: [] as AppointmentWithDetails[],
      };
  const shouldShowSettledDivider =
    status.name === "finalizado" &&
    groupedAppointments.unsettled.length > 0 &&
    groupedAppointments.settled.length > 0;

  return (
    <div className="flex min-h-0 min-w-[260px] max-w-[280px] flex-1 flex-col space-y-3 overflow-x-hidden">
      {/* Cabeçalho da coluna */}
      <div className="flex items-center gap-2">
        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: getStatusColor(status.name, statuses) }} />
        <h3 className="font-semibold text-sm flex-1">{status.display_name}</h3>
        <Badge variant="secondary" className="h-6 px-2 text-xs">
          {appointments.length}
        </Badge>
      </div>

      {/* Wrapper: zona de drop invisível (altura fixa para colisão estável) + layout visível como antes */}
      <div className="relative">
        {/* Drop zone invisível – mesma altura fixa, não altera layout */}
        <div
          ref={setNodeRef}
          className="absolute inset-0 pointer-events-none min-h-[8rem]"
          aria-hidden
        />
        {/* Conteúdo visível – layout igual ao de antes (vazio = caixa baixa, com itens = ScrollArea) */}
        <div
          className={cn(
            "min-w-0 rounded-lg border-2 py-3 transition-all duration-200",
            displayOver
              ? "border-primary bg-primary/10 shadow-lg border-dashed"
              : "border-border bg-white/80"
          )}
        >
          {appointments.length === 0 ? (
            <div
              className={cn(
                "flex items-center justify-center text-sm text-muted-foreground",
                displayOver ? "min-h-[8rem]" : "h-32"
              )}
            >
              Sem agendamentos nesta coluna
            </div>
          ) : (
            <ScrollArea className="flowboard-scroll flowboard-column-scroll h-full min-w-0">
              <div className="min-w-0 space-y-2 px-2">
                {groupedAppointments.unsettled.map((appointment) => (
                  <AppointmentKanbanCard
                    key={appointment.id}
                    appointment={appointment}
                    statuses={statuses}
                    professionals={professionals}
                    onClick={() => onAppointmentClick?.(appointment.id)}
                    showAppointmentItemsOnCards={showAppointmentItemsOnCards}
                  />
                ))}
                {shouldShowSettledDivider ? (
                  <div className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="h-px flex-1 bg-border" />
                      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        Pagos
                      </span>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                  </div>
                ) : null}
                {groupedAppointments.settled.map((appointment) => (
                  <AppointmentKanbanCard
                    key={appointment.id}
                    appointment={appointment}
                    statuses={statuses}
                    professionals={professionals}
                    onClick={() => onAppointmentClick?.(appointment.id)}
                    showAppointmentItemsOnCards={showAppointmentItemsOnCards}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </div>
  );
}
